/**
 * StorageServiceFactory
 * 
 * Factory to provide the appropriate storage service based on configuration.
 * This allows switching between localStorage and SQLite database.
 */

import LocalStorageService from './StorageService';
import SQLiteStorageService from './SQLiteStorageService';

class StorageServiceFactory {
  constructor() {
    this.currentService = null;
    this.preferSQLite = true; // Default to preferring SQLite if available
    this.isSQLiteAvailable = null; // null means not checked yet
    this.isInitialized = false;
    this.authTokenGetter = null;
    this.sqliteServiceInstance = null;
  }
  
  /**
   * Set the auth token getter function
   */
  setAuthTokenGetter(getAuthToken) {
    console.log('StorageServiceFactory: Setting auth token getter');
    this.authTokenGetter = getAuthToken;
    // If we're already using SQLite service instance, update it
    if (this.sqliteServiceInstance && this.authTokenGetter) {
      console.log('StorageServiceFactory: Injecting auth token getter into existing SQLite service instance');
      this.sqliteServiceInstance.setAuthTokenGetter(this.authTokenGetter);
    } else {
      console.log('StorageServiceFactory: SQLite service instance not yet created, will inject later');
    }
  }

  /**
   * Check if SQLite database is available (with authentication)
   */
  async checkSQLiteAvailability() {
    if (this.isSQLiteAvailable !== null) {
      return this.isSQLiteAvailable; // Already checked
    }

    if (!this.authTokenGetter) {
      console.log('StorageServiceFactory: Cannot check SQLite availability - no auth token getter available');
      this.isSQLiteAvailable = false;
      return false;
    }

    try {
      // Create SQLite service instance if not exists
      if (!this.sqliteServiceInstance) {
        this.sqliteServiceInstance = new SQLiteStorageService();
        this.sqliteServiceInstance.setAuthTokenGetter(this.authTokenGetter);
      }
      
      console.log('StorageServiceFactory: Checking SQLite database availability with authentication');
      this.isSQLiteAvailable = await this.sqliteServiceInstance.isDatabaseAvailable();
      console.log(`StorageServiceFactory: SQLite database ${this.isSQLiteAvailable ? 'is' : 'is not'} available`);
      
      return this.isSQLiteAvailable;
    } catch (error) {
      console.error('StorageServiceFactory: Error checking SQLite availability', error);
      this.isSQLiteAvailable = false;
      return false;
    }
  }

  /**
   * Initialize the storage service (without checking SQLite availability yet)
   * 
   * @returns {Promise<void>}
   */
  async initialize() {
    if (this.isInitialized) {
      return;
    }

    console.log('StorageServiceFactory: Initializing (SQLite availability will be checked when auth is ready)');
    
    // Start with localStorage as default until we can check SQLite
    this.currentService = LocalStorageService;
    this.isInitialized = true;
  }

  /**
   * Get the current storage service
   * 
   * @returns {object} The storage service
   */
  async getService() {
    if (!this.isInitialized) {
      await this.initialize();
    }
    
    // If we haven't checked SQLite availability yet and auth is available, check now
    if (this.preferSQLite && this.isSQLiteAvailable === null && this.authTokenGetter) {
      const sqliteAvailable = await this.checkSQLiteAvailability();
      
      if (sqliteAvailable) {
        console.log('StorageServiceFactory: Switching to SQLite storage service');
        this.currentService = this.sqliteServiceInstance;
      }
    }
    
    // If using SQLite, wait for auth token getter to be available
    if (this.isUsingSQLite()) {
      const maxWaitTime = 15000; // 15 seconds
      const checkInterval = 100; // 100ms
      let waitTime = 0;
      
      while (!this.authTokenGetter && waitTime < maxWaitTime) {
        console.log('StorageServiceFactory: Waiting for auth token getter to be set...');
        await new Promise(resolve => setTimeout(resolve, checkInterval));
        waitTime += checkInterval;
      }
      
      if (!this.authTokenGetter) {
        console.error('StorageServiceFactory: Auth token getter not available after waiting - falling back to localStorage');
        this.currentService = LocalStorageService;
        return this.currentService;
      }
      
      // Ensure the SQLite service has the auth token getter
      if (this.sqliteServiceInstance && !this.sqliteServiceInstance.getAuthToken) {
        console.log('StorageServiceFactory: Injecting auth token getter into SQLite service');
        this.sqliteServiceInstance.setAuthTokenGetter(this.authTokenGetter);
      }
    }
    
    return this.currentService;
  }

  /**
   * Force using localStorage regardless of SQLite availability
   * 
   * @returns {object} The localStorage service
   */
  useLocalStorage() {
    this.currentService = LocalStorageService;
    console.log('StorageServiceFactory: Forced using localStorage service');
    return this.currentService;
  }

  /**
   * Force using SQLite if available
   * 
   * @returns {object} The SQLite service if available, otherwise localStorage
   */
  async useSQLite() {
    if (!this.isInitialized) {
      await this.initialize();
    }
    
    // Check SQLite availability if not checked yet
    if (this.isSQLiteAvailable === null) {
      await this.checkSQLiteAvailability();
    }
    
    if (!this.isSQLiteAvailable) {
      console.warn('StorageServiceFactory: SQLite database is not available, falling back to localStorage');
      return LocalStorageService;
    }
    
    this.currentService = this.sqliteServiceInstance;
    console.log('StorageServiceFactory: Forced using SQLite service');
    return this.currentService;
  }

  /**
   * Check if SQLite is being used
   * 
   * @returns {boolean} True if using SQLite, false if using localStorage
   */
  isUsingSQLite() {
    return this.currentService === this.sqliteServiceInstance;
  }
}

// Create singleton instance
const storageServiceFactory = new StorageServiceFactory();

export default storageServiceFactory;